import Color from "@arcgis/core/Color";
import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import { PictureMarkerSymbol } from "@arcgis/core/symbols";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import * as labelPointOperator from "@arcgis/core/geometry/operators/labelPointOperator.js";

export function createDropoffPointsLayer() {
       // Define the image symbol
       const dropOffPictureSymbol = new PictureMarkerSymbol({
        url: "https://livefarmpublic.s3.eu-west-1.amazonaws.com/dropoff_blue.png", // URL to your image
        width: "24px", // Size of the image
        height: "24px",
      });
  
      // Create a renderer using the picture marker symbol
      const dropOffRenderer = new SimpleRenderer({
        symbol: dropOffPictureSymbol,
      });
  
    const dropOff = new FeatureLayer({
        url: "https://services5.arcgis.com/yHz1hcaeOFbAIEo2/arcgis/rest/services/ISEC_DropOff_Points/FeatureServer",
        labelingInfo: [
          {
            labelPlacement: "below-center",
            labelExpressionInfo: {
              expression: "$feature.USER_DropO",
            },
            maxScale: 0,
            minScale: 1200000,
            symbol: {
              type: "text",
              color: "#00c5ff",
              width: "20px",
              height: "20px",
              font: {
                size: "13px",
                family: "Arial",
                weight: "bold",
              },
            },
          },
        ],
        renderer: dropOffRenderer,
      });

      dropOff.when(() => {
        dropOff.renderer.symbol.width = 20;
        dropOff.renderer.symbol.height = 20;
      });

    return dropOff;
}

export function createSamplerLocationLayer() {

    // Define the image symbol
    const samplerPictureSymbol = new PictureMarkerSymbol({
        url: "https://livefarmpublic.s3.eu-west-1.amazonaws.com/sampler.png", // URL to your image
        width: "24px", // Size of the image
        height: "24px",
      });
  
      // Create a renderer using the picture marker symbol
      const samplerRenderer = new SimpleRenderer({
        symbol: samplerPictureSymbol,
      });
  
    const samplerLocation =  new FeatureLayer({
        url: "https://services5.arcgis.com/yHz1hcaeOFbAIEo2/arcgis/rest/services/Sampler_Locations_ActiveSamplers/FeatureServer",
        labelingInfo: [
          {
            labelPlacement: "below-center",
            labelExpressionInfo: {
              expression: "$feature.USER_Sampl",
            },
            maxScale: 0,
            minScale: 1200000,
            symbol: {
              type: "text",
              color: "#4ce600",
              font: {
                size: "13px",
                family: "Arial",
                weight: "bold",
              },
            },
          },
        ],
        renderer: samplerRenderer,
      });


      samplerLocation.when(() => {
        samplerLocation.renderer.symbol.width = 20;
        samplerLocation.renderer.symbol.height = 20;
      });

      return samplerLocation;

}

export function createSampleGraphic(path) {
    
        const polyline = {
          type: "polyline",
          paths: path.paths_xy,
          spatialReference: {
            wkid: 3857,
          },
        };

        // Create a symbol for rendering the graphic
        const fillSymbol = {
          type: "simple-fill",
          color: new Color("rgba(51, 51,204, 0.3)"),
          outline: {
            color: new Color("rgba(255, 255, 255, 0.9)"),
            width: 1,
          },
        };

        const labelText = {
          type: "text", // autocasts as new TextSymbol()
          color: "white",
          text: path.sample_id,
          font: {
            // autocasts as new Font()
            size: 12,
            family: "Arial",
            weight: "bold",
          },
        };

        let attributes = {};

        if (path?.type) {
          attributes = {
            type: "edit",
          };
        }

        const samplePath = new Graphic({
          geometry: polyline,
          symbol: fillSymbol,
          attributes: attributes,
        });

        const sampleLabel = new Graphic({
          geometry: samplePath.geometry.extent.center,
          symbol: labelText,
        });

        return {samplePath, sampleLabel};
}

export function createPathsGraphic(path) {
  // Validate path data
    if (!path || !path.paths_xy || !Array.isArray(path.paths_xy) || path.paths_xy.length === 0) {
      console.warn('Invalid path data provided to createPathsGraphic:', path);
      return { samplePath: null, sampleLabel: null, georeferenceMarkers: [] };
    }

    const polyline = {
        type: "polyline",
        paths: path.paths_xy,
        spatialReference: {
          wkid: 3857,
        },
      };

      const lineSymbol = {
        type: "simple-line",
        color: [255, 0, 0],
        width: 1,
      };

        const labelText = {
        type: "text", // autocasts as new TextSymbol()
        color: "white",
        text: path.sample_id,
        font: {
          // autocasts as new Font()
          size: 12,
          family: "Arial",
          weight: "bold",
        },
      };

      const samplePath = new Graphic({
        geometry: polyline,
        symbol: lineSymbol,
        attributes: {
          sample_id: path.sample_id,
          type: "sample_path",
          farm_name: path.farm_name || 'Unknown Farm',
          lab_tests: Array.isArray(path.lab_tests) ? path.lab_tests.join(', ') : (path.lab_tests || 'No tests'),
          matched: path.matched ? 'Yes' : 'No'
        },
        popupTemplate: {
          title: "{sample_id}",
          content: `
            <div class="popup-content">
              <div class="popup-section">
                <strong>ID:</strong> {sample_id}<br>
                <strong>Farm:</strong> {farm_name}
              </div>
              <div class="popup-section">
                <strong>Zone Match:</strong> {matched}<br>
                <strong>Lab Tests:</strong> {lab_tests}
              </div>
              <div class="popup-section">
                <strong>Georeference Markers:</strong> ${path.marker_coordinates && path.marker_coordinates.length ? path.marker_coordinates.length : 0}
              </div>
            </div>
          `
        }
      });

      // Place label at actual midpoint of the path
      const midIdx = Math.floor(path.paths_xy[0].length / 2); // Fixed: was dividing by 3
      const sampleLabel = new Graphic({
        geometry: {
          type: "point",
          x: path.paths_xy[0][midIdx][0],
          y: path.paths_xy[0][midIdx][1],
          spatialReference: { wkid: 3857 }
        },
        symbol: labelText,
      });

      // Create georeference markers if available
      const georeferenceMarkers = createGeoreferenceMarkerGraphics(path);

      return {samplePath, sampleLabel, georeferenceMarkers};
}

export function createParcelGraphic(parcel) {
    const polygon = {
        type: "polygon",
        rings: parcel.boundaries,
        spatialReference: {
          wkid: 3857,
        },
      };

      // Create a symbol for rendering the graphic
      let fillColor = parcel.fillColor;
      
      // Handle null/transparent fills
      if (!parcel.fillColor || 
          parcel.fillColor[3] === 0 || 
          parcel.fillColor[3] === undefined ||
          (parcel.fillColor[0] >= 250 && parcel.fillColor[1] >= 250 && parcel.fillColor[2] >= 250)) {
        fillColor = [0, 0, 0, 0]; // Completely transparent fill
      }
      
      const fillSymbol = {
        type: "simple-fill",
        color: fillColor,
        outline: {
          color: [255, 255, 255],
          width: 1,
        },
      };

      const labelText = {
        type: "text", // autocasts as new TextSymbol()
        color: "white",
        text: parcel.sampleId,
        font: {
          // autocasts as new Font()
          size: 12,
          family: "Arial",
          weight: "bold",
        },
      };
      // Add the geometry and symbol to a new graphic
      const polygonGraphic = new Graphic({
        geometry: polygon,
        symbol: fillSymbol,
      });

      const sampleLabel = new Graphic({
        geometry: polygonGraphic.geometry.extent.center,
        symbol: labelText,
      });

      return  {polygonGraphic, sampleLabel};
}

export function createParcelEditingGraphic(path) {
  const polygon = {
    type: "polygon",
    rings: [path.paths_xy], // Use rings for polygons, not paths
    spatialReference: {
      wkid: 3857,
    },
  };

  const fillSymbol = new SimpleFillSymbol({
    color: new Color("rgba(51, 51, 204, 0.3)"),
    style: "solid",
    outline: new SimpleLineSymbol({
      color: new Color("rgba(255, 255, 255, 0.9)"),
      width: 2,
    }),
  });

  // Include the hashid in attributes for matching with original graphic
  const attributes = {
    type: "edit",
    hashid: path.hashid,
    sample_id: path.sample_id,
  };

  const editingGraphic = new Graphic({
    geometry: polygon,
    symbol: fillSymbol,
    attributes: attributes,
  });

  return editingGraphic;
}

export function createGeoreferenceMarkerGraphics(path) {
  const markers = [];
  
  // Consistent georeference color across the application
  const GEOREFERENCE_COLOR_RGB = [220, 38, 38, 0.8]; // #DC2626 in RGB format
  
  if (!path.marker_coordinates || !Array.isArray(path.marker_coordinates)) {
    return markers;
  }

  // Create marker graphics for each georeference point
  path.marker_coordinates.forEach(marker => {
    const point = {
      type: "point",
      longitude: marker.longitude,
      latitude: marker.latitude,
      spatialReference: { wkid: 4326 }
    };

    const markerSymbol = {
      type: "simple-marker",
      style: "circle",
      color: GEOREFERENCE_COLOR_RGB, // Red color for better visibility
      size: "12px",
      outline: {
        color: [255, 255, 255, 1],
        width: 2
      }
    };

    const markerGraphic = new Graphic({
      geometry: point,
      symbol: markerSymbol,
      attributes: {
        sample_id: marker.sample_id,
        marker_index: marker.index,
        type: "georeference_marker"
      },
      popupTemplate: {
        title: "Georeference Marker",
        content: `
          <div class="popup-content">
            <div class="popup-section">
              <strong>Sample ID:</strong> {sample_id}<br>
              <strong>Marker Index:</strong> {marker_index}<br>
              <strong>Type:</strong> DAFM Compliance Marker
            </div>
            <div class="popup-section">
              <strong>Coordinates:</strong><br>
              <small>Latitude:</small> ${marker.latitude.toFixed(6)}<br>
              <small>Longitude:</small> ${marker.longitude.toFixed(6)}
            </div>
            <div class="popup-note">
              <small><em>Green markers indicate georeference points required for DAFM compliance</em></small>
            </div>
          </div>
        `
      }
    });

    markers.push(markerGraphic);
  });

  return markers;
}